import type { CommsBackend } from '../lib/config';

interface CommsBackendBadgeProps {
  // The worker's resolved comms backend. Sourced from graphy admin data
  // (the `comms_backend` field on the admin worker payload). When absent —
  // e.g. the backend hasn't shipped the field yet, or the worker isn't
  // allowlisted — we treat it as PubNub (the fail-safe default).
  backend?: CommsBackend | null;
  // When true, render nothing for PubNub workers (only the Supabase canary
  // cohort is highlighted). Handy in dense lists where the default backend
  // is implied.
  hidePubnub?: boolean;
}

const baseStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  borderRadius: 10,
  padding: '1px 7px',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  lineHeight: '16px',
  whiteSpace: 'nowrap',
};

const supabaseStyle: React.CSSProperties = {
  ...baseStyle,
  background: '#ecfdf5',
  border: '1px solid #a7f3d0',
  color: '#047857',
};

const pubnubStyle: React.CSSProperties = {
  ...baseStyle,
  background: '#f3f4f6',
  border: '1px solid #e5e7eb',
  color: '#6b7280',
};

/**
 * Small per-worker indicator so ops can tell, at a glance, whether a worker
 * is on the Supabase canary or still on PubNub — and therefore which chat
 * surface to open. Purely presentational: pass the backend value in.
 */
export function CommsBackendBadge({
  backend,
  hidePubnub = false,
}: CommsBackendBadgeProps) {
  const resolved: CommsBackend = backend === 'supabase' ? 'supabase' : 'pubnub';

  if (resolved === 'pubnub' && hidePubnub) return null;

  if (resolved === 'supabase') {
    return (
      <span style={supabaseStyle} title="This worker is on Supabase comms">
        Supabase
      </span>
    );
  }

  return (
    <span style={pubnubStyle} title="This worker is on PubNub comms">
      PubNub
    </span>
  );
}

export default CommsBackendBadge;
